import { ENGATI_ICON_URL } from 'consts/consts';
import { BOT_MODE, FONT_SIZE, HEADER_TYPE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import {
	debug,
	deleteFromLs,
	downloadIndieFlowerFont,
	downloadRobotoFont,
	generateUUID,
	getSaturation,
	hexToRgb,
	onEditResponse,
	readFromLs,
	showCallout,
	updateBotLanguageToLS,
	writeToLs,
} from 'helpers/helpers';
import { slider } from 'icons/slider';
import { ENGT_CHAT } from 'js/bot.model';
import { appendToChat } from 'js/conversation';
import { setupSpeech } from './speech';
import { voiceBotInit } from './voiceBot';

const updateFontFamily = function (fontFamily, fontWeight) {
	const themeFontOptions = {
		arial: 'Arial, Helvetica, Sans-serif',
		brushScriptMt: 'Brush Script MT, Italic, Cursive',
		calibri: 'Calibri, Arial, Sans-serif',
		copperplate: 'Copperplate, Romanserif, Sans-serif',
		courierNew: 'Courier New, Courier, Copperplate',
		garamond: 'Garamond, Serif',
		helvetica: 'Helvetica, Serif, Sans-serif',
		indieFlower: 'Indie Flower, Cursive',
		nunito: 'Nunito, Avenir, Sans-serif',
		openSans: 'Open Sans, Sans-serif, Sans',
		raleway: 'Raleway, Avenir, Sans-serif',
		roboto: 'Roboto, Comic Sans MS, Sans',
		timesNewRoman: 'Times New Roman, Times, Serif',
		verdana: 'Verdana, Arial, Sans-serif',
		zapfChancery: 'Zapf Chancery, Cursive',
	};

	let fontFamilyCls = 'Hind';

	if ((fontFamily === 'helvetica' || fontFamily === 'garamond') && fontWeight === 'LIGHT') {
		fontFamilyCls = `${themeFontOptions[fontFamily]}; font-weight: 300`;
	} else if (fontFamily === 'indieFlower') {
		downloadIndieFlowerFont();
		fontFamilyCls = '"Indie Flower script=all rev=2", "Adobe Blank"';
	} else if (fontFamily === 'roboto') {
		downloadRobotoFont();
		fontFamilyCls = themeFontOptions[fontFamily];
	} else {
		fontFamilyCls = themeFontOptions[fontFamily];
	}

	const style = document.createElement('style');
	style.appendChild(
		document.createTextNode(
			`
			.engt * {
				font-family: ${fontFamilyCls} !important;
			}`
		)
	);

	if (fontFamilyCls) {
		document.head.appendChild(style);
	}

	return fontFamilyCls;
};

const brandingSetup = (resp) => {
	ENGT_CHAT.set('config.remove_branding', resp.remove_branding);
	if (typeof ENGT_CHAT.get('config.set_default_brand') === 'object') {
		Object.keys(ENGT_CHAT.get('config.set_default_brand')).length && ENGT_CHAT.set('config.remove_branding', false);
	} else if (ENGT_CHAT.get('config.set_default_brand')) {
		ENGT_CHAT.set('config.remove_branding', false);
	}
};

const updateThemeCss = () => {
	const {
		themeColor,
		themeTextColor,
		userResponseBubbleColor,
		userResponseTextColor,
		botResponseBubbleColor,
		botResponseTextColor,
		buttonSelectorColor,
	} = ENGT_CHAT.get('config');

	const sliderBgCss = `background: url("data:image/svg+xml, ${slider(buttonSelectorColor.replace('#', '%23'))}")`;
	const isBotImageAvailable = !!ENGT_CHAT.get('config.chat_header_icon_url');
	const root = document.documentElement;
	const style = document.createElement('style');
	const isStandalone = ENGT_CHAT.get('config.bot_mode') === BOT_MODE.STANDALONE;
	const isSmallFont = readFromLs(LS().FONT_SETTING) === FONT_SIZE.SMALL;
	style.type = 'text/css';
	root.style.setProperty('--engt-mic-bg-color', hexToRgb(themeColor, 0.1));
	root.style.setProperty('--engt-theme-color', themeColor);
	root.style.setProperty('--engt-theme-text-color', themeTextColor);
	root.style.setProperty('--engt-user-bubble-color', userResponseBubbleColor);
	root.style.setProperty('--engt-bot-bubble-color', botResponseBubbleColor);
	root.style.setProperty('--engt-user-text-color', userResponseTextColor);
	root.style.setProperty('--engt-bot-text-color', botResponseTextColor);
	root.style.setProperty('--engt-button-bg-color', buttonSelectorColor);
	root.style.setProperty('—-engt-input-border-color', '#eeeecd');
	root.style.setProperty('--engt-red', '#ae1536');
	root.style.setProperty('--engt-img-msg-height', isStandalone ? '320px' : '125px');
	root.style.setProperty('--engt-video-msg-height', isStandalone ? '180px' : '125px');
	style.innerHTML = `.engt-slider-input::-webkit-slider-thumb { ${sliderBgCss} }`;
	style.innerHTML += `.engt-slider-input::-moz-range-thumb { ${sliderBgCss} }`;
	if (isSmallFont) {
		root.style.setProperty('--engt-launch-screen-header', isBotImageAvailable ? '21.5em' : '16.5em');
	} else {
		root.style.setProperty('--engt-launch-screen-header', isBotImageAvailable ? '20em' : '16em');
	}

	document.head.appendChild(style);
};

export const onConfigBotResponse = (resp) => {
	if (!resp) {
		debug('No response came from Bot Config API');
		return;
	}

	const localConfig = {
		...ENGT_CHAT.get('config'),
		brand_name: resp['ENGATI_PLATFORM_NAME'],
		themeColor: resp['ENGATI_WEBCHAT_THEME_COLOR'],
		poweredByBrandName: resp['ENGATI_POWERED_BY_BRAND_NAME'],
		poweredByRedirectionURL: resp['ENGATI_POWERED_BY_REDIRECTION_URL'],
		website_url: resp.website_url,
		suggestEnabled: resp.suggest_enabled,
		suggestEnabledForBot: resp.suggest_enabled_for_bot,
		suggestDebounceDelayMs: resp.suggest_debounce_delay_ms,
		suggestMinLength: resp.suggest_min_length,
		suggestMinWords: resp.suggest_min_words,
		suggestMaxResponses: resp.suggest_max_responses,
		isLaunchScreenEnabled: resp.is_launch_screen_enabled,
		isFadingMessagesEnabled: resp.is_fading_messages_enabled,
		isCtaResponseHidden: !!resp.is_cta_response_hidden,
		isRudRespondBoxEnabled: resp.is_response_box_rud_enabled || false,
		botRef: resp.bot_ref,
		launchScreenTitle: resp.launch_screen_title,
		launchScreenHeader: resp.launch_screen_header || resp.bot_name,
		launchCardType: resp.launch_cards_type,
		showAgentProfiles: resp.show_agent_profiles,
		showAgentResponsiveness: resp.show_agent_responsiveness,
		enableFaqIframes: resp.enable_faq_iframes,
		queryTitle: resp.query_title,
		queryPlaceholder: resp.query_placeholder,
		launchCards: resp.launch_cards || [],
		enableAutoSuggestionEntitlement: resp.enable_auto_suggestion_entitlement,
		onlineAgentList: resp.online_agents && resp.online_agents.agent_details ? resp.online_agents.agent_details : [],
		is_unified_module: resp.is_unified_module,
		chat_close_button_position: resp.chat_close_button_position,
		ttonClass: resp.chat_close_button_position === 'BOTTOM' ? '' : 'engt-visible',
		userResponseBubbleColor: resp.chat_bubble_color || resp.theme_color,
		botResponseBubbleColor: resp.bot_response_bubble_color || '#f6f6f6',
		botResponseTextColor: resp.bot_response_bubble_text_color || '#455a64',
		userResponseTextColor: resp.chat_bubble_text_color || getSaturation(resp.chat_bubble_color || resp.theme_color),
		buttonSelectorColor: resp.button_selector_color || '#1AABED',
		buttonTextColor: getSaturation(resp.button_selector_color || '#1AABED'),
		textAreaColor: resp.text_area_color || '#ffffff',
		is_agent_notifications_enabled: resp.is_agent_notifications_enabled,
		isNotified: false,
		enable_trigger_analytics_config: resp.enable_trigger_analytics_config,
		is_crm_update: resp.is_crm_update,
		enabledLanguages: resp.enabled_languages,
		edit_user_input: resp.edit_user_input,
		default_edit_response: resp.default_edit_response,
		isAnimating: false,
		icon_url: resp.icon_url && resp.icon_url.trim() !== '' ? resp.icon_url : ENGATI_ICON_URL,
		launch_flow_copy: ENGT_CHAT.get('config.launch_flow'),
		bot_name: resp.bot_name,
		header_description: resp.header_description,
		header_alignment: resp.header_alignment,
		blockSendMessageWithOptions: resp.block_send_message_with_options ?? false,
		background_option: resp.background_option,
		top_stop_color: resp.top_stop_color || '#FFFFFF',
		bottom_stop_color: resp.bottom_stop_color || '#FFFFFF',
		mid_stop_color: resp.mid_stop_color || '#FFFFFF',
		wallpaper_image_url: resp.wallpaper_image_url,
		autoPopupType: resp.auto_pop_up_web_chat_type,
		autoPopupDelay: resp.auto_enable_pop_up_delay,
		popupMessage: resp.pop_up_message,
		themeFont: resp.theme_font,
		headerType: resp.header_type || HEADER_TYPE.TEXT_WITH_IMAGE,
		botLanguage: resp.locale || 'default',
		showReplyArea: resp.show_reply_area ?? true,
		showClearConversationHistory: resp.refresh_chat,
		enableUploadAttachment: resp.enable_attachment_upload ?? false,
		isMultilingualEnabled: resp.enabled_languages && resp.enabled_languages.length > 1,
		localeForBotIsChanged: false,
		isFaqAutocompleteEnabled: resp.is_autocomplete_enabled,
		disableSmoOptions: resp.disable_smo_options ?? false,
		is_debug: ENGT_CHAT.get('config.debug') ?? false,
		isConversationalIvrEnabled: resp.is_conversational_ivr_enabled,
		isLLMEnabled: resp.is_llm_enabled ?? false,
		isWebLinkPreviewEnabled: resp.is_website_link_preview_enabled ?? false,
		isPdfLinkPreviewEnabled: resp.is_pdf_link_preview_enabled ?? false,
		isVoiceLLMConfigEnabled: resp.is_voice_llm_enabled ?? false,
		azureVoiceIntegrationKey: resp.azure_voice_integration_key || '',
		azureVoiceIntegrationRegion: resp.azure_voice_integration_region || '',
		...(resp.chat_widget_position && {
			chat_widget_position: resp.chat_widget_position,
		}),
		...(resp.theme_color && {
			themeColor: resp.theme_color,
		}),
		...(resp.welcome_msg && {
			welcome_msg: resp.welcome_msg,
			welcomeMessageCopy: resp.welcome_msg,
		}),
	};

	if (resp.custom_js_link && !document.getElementById('engtCustomScript')) {
		const customScript = document.createElement('script');
		customScript.id = 'engtCustomScript';
		customScript.defer = true;
		customScript.src = resp.custom_js_link;
		document.body.append(customScript);
	}

	if (resp.custom_css_link && !document.getElementById('engtCustomStyles')) {
		const customCss = document.createElement('link');
		customCss.id = 'engtCustomStyles';
		customCss.rel = 'stylesheet';
		customCss.href = resp.custom_css_link;
		document.head.append(customCss);
	}

	if (!readFromLs(LS().GOT_UID)) {
		writeToLs(LS().GOT_UID, 'false');
	}

	if (readFromLs(LS().TAB_MESSAGES)) {
		deleteFromLs(LS().TAB_MESSAGES);
	}

	if (resp.header_text_color) {
		localConfig.themeTextColor = resp.header_text_color;
	} else if (localConfig.title_color) {
		localConfig.themeTextColor = localConfig.title_color;
	} else {
		localConfig.themeTextColor = getSaturation(localConfig.themeColor);
	}

	if (resp.bot_avatar_image_url && resp.bot_avatar_image_url.trim() !== '') {
		localConfig.bot_avatar_image_url = resp.bot_avatar_image_url;
	}
	if (resp.chat_header_icon_url && resp.chat_header_icon_url.trim() !== '') {
		localConfig.chat_header_icon_url = resp.chat_header_icon_url;
	}
	if (resp.enable_ip_address) {
		localConfig.enableIpAddress = resp.enable_ip_address;
	}

	if (resp.input_area_suggestion_text) {
		localConfig.textAreaPlaceholder = resp.input_area_suggestion_text;
	}

	try {
		localConfig.webMenu = JSON.parse(resp.web_menu);
	} catch (error) {
		localConfig.webMenu = null;
	}

	ENGT_CHAT.set('config', localConfig);

	if (ENGT_CHAT.get('config.isConversationalIvrEnabled')) {
		const azureVoiceScript = document.createElement('script');
		azureVoiceScript.src = 'https://aka.ms/csspeech/jsbrowserpackageraw';
		azureVoiceScript.onload = voiceBotInit;
		document.body.append(azureVoiceScript);
	}

	// If the locale for a bot changed from the dropdown in launch screen
	// then skip the local storage part
	if (!readFromLs(LS().USER_LOCALE)) {
		// Setting the bot default language
		updateBotLanguageToLS(resp.locale);
	}

	if (readFromLs(LS().NEW_SESSION)) {
		ENGT_CHAT.set('status.sessionKey', readFromLs(LS().NEW_SESSION));
		ENGT_CHAT.set('firstTime', false);
	} else {
		ENGT_CHAT.set('status.sessionKey', generateUUID());
		writeToLs(LS().NEW_SESSION, ENGT_CHAT.get('status.sessionKey'));
		ENGT_CHAT.set('firstTime', true);
	}

	ENGT_CHAT.set('fadingMessageQueue', []);
	if (ENGT_CHAT.get('config.isFadingMessagesEnabled')) {
		ENGT_CHAT.set(
			'fadingMessageInterval',
			setInterval(() => {
				if (ENGT_CHAT.get('fadingMessageQueue').length) {
					const queue = ENGT_CHAT.get('fadingMessageQueue');
					const { data, config } = queue.shift() || {};

					appendToChat(data, config, false);
					ENGT_CHAT.set('fadingMessageQueue', queue);
				}
			}, 400)
		);
	}

	updateFontFamily(resp.theme_font, resp.font_weight);
	setupSpeech(resp);
	brandingSetup(resp);
	updateThemeCss();

	if (ENGT_CHAT.get('config.edit_user_input')) {
		ENGT_CHAT.set('onEditResponse', onEditResponse);
	}

	if (
		(resp.activity_tracking_config && resp.activity_tracking_config.enabled) ||
		(resp.engagement_triggers_config && resp.engagement_triggers_config.enabled)
	) {
		ENGT_CHAT.set('config.activity_tracking_config', resp.activity_tracking_config);
		ENGT_CHAT.set('config.engagement_triggers_config', resp.engagement_triggers_config);
		if (resp.trigger_configurations && resp.trigger_configurations.length) {
			ENGT_CHAT.set('config.trigger_configurations', resp.trigger_configurations);
		}
		window.EngtChat = {
			config: ENGT_CHAT.get('config'),
			showCallout,
		};
	}
};
